import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, Button, Tabs, TabList, TabPanels, Tab, TabPanel, VStack, HStack, Divider, IconButton, Icon, Link, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, useToast
} from '@chakra-ui/react';
import { useDrag, useDrop } from 'react-dnd';
import { ChevronDownIcon, LockIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { useDisclosure } from '@chakra-ui/react';
import { useTeamLineup, useTeamPitchingRotation, PlayerType, LineupType, SimBaserunningEventRow, SimFieldingEventRow, fetchTeamExtendedBench } from '../hooks/useLineups';
import { useMyLeagueStore, isLineupEditingDisabled } from '../store/myLeagueStore';
import { useUserStore } from '../store/userStore';
import { isAdmin } from '../hooks/useUser';
import { attachSimStats, buildPitcherSimMap } from '../lib/pitcherSimStats';
import { useModernToast } from './ModernToast';

// CSS classes for position colors
const positionColorStyles = `
  .position-primary {
    color: white;
  }
  .position-secondary {
    color: #63b3ed;
  }
  .position-tertiary {
    color: #F6AD55;
  }
  .position-other {
    color: #E53E3E;
  }
`;

const positions = ['P', 'CA', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
const positionsDH = ['CA', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];

// const fakeInactivePlayers = [
//   { name: 'Inactive Player 1', position: 'CF, LF, RF' },
//   { name: 'Inactive Player 2', position: 'CF, LF, RF' },
//   { name: 'Inactive Player 3', position: 'CF, LF, RF' },
//   { name: 'Inactive Player 4', position: 'CF, LF, RF' },
//   { name: 'Inactive Player 5', position: 'CF, LF, RF' },
//   { name: 'Inactive Player 6', position: 'CF, LF, RF' },

// ];

const darkBg = '#181A20';
const darkTableBg = '#23262F';
const darkText = 'white';
const darkSecondary = 'gray.400';

function LineupWarningBanner({ show, message }: { show: boolean; message: string }) {
  if (!show) return null;
  return (
    <Flex align="center" gap={2} mb={3} color="red.400">
      <Icon as={WarningTwoIcon} color="red.400" boxSize={5} flexShrink={0} aria-hidden />
      <Text fontWeight="semibold">{message}</Text>
    </Flex>
  );
}

const ItemTypes = {
  BATTER: 'batter',
  BENCH: 'bench',
  PITCHER: 'pitcher',
};

type PlayerTypeWithRosterId = PlayerType & { roster_player_id?: number, firstName?: string, lastName?: string };

type BattingOrderRowProps = {
  slot: PlayerTypeWithRosterId;
  idx: number;
  moveBatter: (fromIdx: number, toIdx: number) => void;
  onDropFromBench: (benchIdx: number, toIdx: number) => void;
  positions?: string[];
  columnHeaders: string[];
  editingPosIdx: number | null;
  setEditingPosIdx: (idx: number | null) => void;
  setBattingOrder: (order: PlayerTypeWithRosterId[]) => void;
  pitchers: PlayerType[];
  battingOrder: PlayerTypeWithRosterId[];
  commitLineupUpdate: (entries: PlayerTypeWithRosterId[], previousBattingOrder: PlayerTypeWithRosterId[]) => void;
  simEvents?: SimEvent[];
  simBaserunningEvents?: SimBaserunningEventRow[];
  simFieldingEvents?: SimFieldingEventRow[];
  lineupEditsDisabled?: boolean;
};

type BenchRowProps = {
  player: PlayerType;
  benchIdx: number;
  simEvents?: SimEvent[];
  simBaserunningEvents?: SimBaserunningEventRow[];
  simFieldingEvents?: SimFieldingEventRow[];
  lineupEditsDisabled?: boolean;
};

/** One row from getUnusedBatterEvents: roster_player_id, mlb_id, unused_pa_total, dom_play_totals. */
type SimEvent = {
  roster_player_id?: number;
  mlb_id: number;
  unused_pa_total: number;
  dom_play_totals?: Record<string, number>;
};

/** Linear weights from dom_play_totals. Returns null when no data (show "-"). */
function getLinearWeight(dom_play_totals: Record<string, number> | undefined): number | null {
  if (!dom_play_totals || typeof dom_play_totals !== 'object') return null;
  const get = (keys: string[]): number =>
    keys.reduce((sum, k) => sum + (Number(dom_play_totals[k]) || 0), 0);
  const oneB = get(['1B']);
  const twoB = get(['2B']);
  const threeB = get(['3B']);
  const hr = get(['HR']);
  const bb = get(['BB', 'IBB']);
  const hbp = get(['HBP']);
  const sb = get(['SB']);
  const cs = get(['CS']);
  const sf = get(['SF']);
  const sac = get(['SAC']);
  // Out types (excludes SF and SAC; those have their own LW terms)
  const outs = get(['OUT', 'K', 'K_S', 'K_L', 'k', 'k_s', 'k_l', 'FB', 'GB', 'OUT_GB', 'FBC', 'GDP']);
  return 0.47 * oneB + 0.78 * twoB + 1.09 * threeB + 1.4 * hr + 0.33 * bb + 0.34 * hbp + 0.2 * sb - 0.4 * cs - 0.28 * outs + 0.37 * sf - 0.09 * sac;
}

/** Coefficients for unused baserunning plays (Bases column); same pattern as LWCoefficients in gameSimulator.js. */
const BasesCoefficients: Record<string, number> = {
  'RBP': 0.08,
  'AAB': 0.18,
  'LAB': -0.18,
  'RUO': -0.58,
  'SB': 0.2,
  'CS': -0.4,
};

function getBasesLinearWeight(dom_play_totals: Record<string, number> | undefined): number {
  if (!dom_play_totals || typeof dom_play_totals !== 'object') return 0;
  return Object.entries(BasesCoefficients).reduce(
    (sum, [key, coef]) => sum + (Number(dom_play_totals[key]) || 0) * coef,
    0
  );
}

/** Unused fielding plays (FLD column): sum of count(dom_play) * coefficient. */
const FieldCoefficients: Record<string, number> = {
  EC: -0.48,
  ET: -0.48,
  CS: 0.4,
  LB: -0.18,
  LO: -0.48,
  LOO: -0.35,
  PAB: 0.18,
  RUO: 0.35,
  GR: -0.48,
};

function getFieldingLinearWeight(dom_play_totals: Record<string, number> | undefined): number {
  if (!dom_play_totals || typeof dom_play_totals !== 'object') return 0;
  return Object.entries(FieldCoefficients).reduce(
    (sum, [key, coef]) => sum + (Number(dom_play_totals[key]) || 0) * coef,
    0
  );
}

function findSimRowForPlayer<T extends { roster_player_id?: unknown; mlb_id?: unknown }>(
  rows: T[] | undefined,
  rosterPlayerId: unknown,
  mlbId: unknown
): T | undefined {
  if (!rows?.length) return undefined;
  if (rosterPlayerId != null) {
    const byRp = rows.find((e) => Number(e.roster_player_id) === Number(rosterPlayerId));
    if (byRp) return byRp;
  }
  if (mlbId != null) {
    return rows.find((e) => Number(e.mlb_id) === Number(mlbId));
  }
  return undefined;
}

/** DEF column: catcher c_def when present, else fielder f_def; "-" when both null. */
function getBatterDefDisplay(player: unknown): string {
  if (player == null || typeof player !== 'object') return '-';
  const row = player as Record<string, unknown>;
  const c = row.c_def ?? row.cDef;
  const f = row.f_def ?? row.fDef;
  if (c == null && f == null) return '-';
  if (c != null) {
    const n = Number(c);
    return Number.isFinite(n) ? n.toFixed(2) : '-';
  }
  if (f != null) {
    const n = Number(f);
    return Number.isFinite(n) ? n.toFixed(2) : '-';
  }
  return '-';
}

/** Header label for batter stat columns; DEF shows a visible footnote-style asterisk. */
function batterStatColumnHeaderNode(col: string): React.ReactNode {
  if (col === 'DEF') {
    return (
      <>
        DEF
        <Text as="span" fontWeight="bold" fontSize="sm" ml="2px">
          *
        </Text>
      </>
    );
  }
  return col;
}

function DefColumnFootnote() {
  return (
    <Text
      mt={1.5}
      color={darkSecondary}
      fontSize="xs"
      lineHeight="short"
      textAlign="left"
      pl={2}
    >
      <Text as="span" fontWeight="semibold">*</Text>{' '}
      DEF is YTD defensive value for fielders and YTD catching value for catchers
    </Text>
  );
}

/** Batting-order stat columns: Avail. PA / LW / BR / FLD / DEF (sim events + player c_def/f_def). */
function getBatterStatColumnValue(
  col: string,
  rosterPlayerId: unknown,
  mlbId: unknown,
  simEvents: SimEvent[] | undefined,
  simBaserunningEvents: SimBaserunningEventRow[] | undefined,
  simFieldingEvents: SimFieldingEventRow[] | undefined,
  player?: unknown
): string {
  const ev = findSimRowForPlayer(simEvents, rosterPlayerId, mlbId) ?? null;
  const brEv = findSimRowForPlayer(simBaserunningEvents, rosterPlayerId, mlbId) ?? null;
  const feEv = findSimRowForPlayer(simFieldingEvents, rosterPlayerId, mlbId) ?? null;

  if (col === 'Avail. PA') {
    return ev != null ? String(ev.unused_pa_total) : '-';
  }
  if (col === 'LW') {
    if (!ev) return '-';
    const pa = Number(ev.unused_pa_total);
    if (!Number.isFinite(pa) || pa === 0) return '-';
    const lw = getLinearWeight(ev.dom_play_totals);
    return lw === null ? '-' : lw.toFixed(2);
  }
  if (col === 'BR') {
    if (!brEv) return '-';
    const br = Number(brEv.unused_br_total);
    if (!Number.isFinite(br) || br === 0) return '-';
    const bases = getBasesLinearWeight(brEv.dom_play_totals);
    return bases.toFixed(2);
  }
  if (col === 'FLD') {
    if (!feEv) return '-';
    const n = Number(feEv.unused_fielding_total);
    if (!Number.isFinite(n) || n === 0) return '-';
    const fld = getFieldingLinearWeight(feEv.dom_play_totals);
    return fld.toFixed(2);
  }
  if (col === 'DEF') {
    return getBatterDefDisplay(player);
  }
  return '-';
}

type BatterDnDItem = { idx: number; type: string };
type BenchDnDItem = { benchIdx: number; type: string };
type PitcherDnDItem = { idx: number; type: string };

type BenchSectionProps = {
  bench: PlayerTypeWithRosterId[];
  columnHeaders: string[];
  onDropToBench: (fromIdx: number) => void;
  battingOrder: PlayerTypeWithRosterId[];
  simEvents?: SimEvent[];
  simBaserunningEvents?: SimBaserunningEventRow[];
  simFieldingEvents?: SimFieldingEventRow[];
  lineupEditsDisabled?: boolean;
  onLoadOtherPlayers?: () => void;
  isLoadingOtherPlayers?: boolean;
  showLoadOtherPlayers?: boolean;
  otherPlayers?: PlayerTypeWithRosterId[];
  otherSimEvents?: SimEvent[];
  otherSimBaserunningEvents?: SimBaserunningEventRow[];
  otherSimFieldingEvents?: SimFieldingEventRow[];
};

const PITCHER_COLUMN_HEADERS = ['Avail. IP', 'LW', '']; // last is blank for rest days

type PitchersSectionProps = {
  pitchers: PlayerType[];
  onDropToPitchers: (fromIdx: number) => void;
  battingOrder: PlayerTypeWithRosterId[];
  movePitcher: (fromIdx: number, toIdx: number) => void;
  onSwitchToPitchersTab?: () => void;
  lineupEditsDisabled?: boolean;
};

function getStatusUntilValue(player: any): string | null {
  const v =
    player?.status_until ??
    player?.statusUntil ??
    player?.status_until_date ??
    player?.statusUntilDate ??
    null;
  if (typeof v === 'string' && v.trim().length > 0) return v;
  return null;
}

function formatAvailMD(statusUntil: string): string {
  const d = new Date(statusUntil);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function getPlayerShortName(player: any): string {
  if (player.firstName && player.lastName) {
    return `${player.firstName.charAt(0)}. ${player.lastName}`;
  }
  if (player.first_name && player.last_name) {
    return `${player.first_name.charAt(0)}. ${player.last_name}`;
  }
  if (player.name) return player.name;
  if (player.full_name) return player.full_name;
  return '';
}

function renderBenchPositionCell(p: any) {
  const primary = Array.isArray(p?.primaryPosition) && p.primaryPosition.length > 0
    ? p.primaryPosition.join(',')
    : (p?.position || p?.position_1 || '');

  const secondary = Array.isArray(p?.secondaryPosition) && p.secondaryPosition.length > 0
    ? p.secondaryPosition.join(',')
    : '';

  const tertiary = Array.isArray(p?.tertiaryPosition) && p.tertiaryPosition.length > 0
    ? p.tertiaryPosition.join(',')
    : '';

  const allSecondary = [secondary, tertiary].filter(Boolean).join(',');

  return (
    <>
      {primary}
      {allSecondary && (
        <Text as="span" color="gray.400" ml={1}>
          ({allSecondary})
        </Text>
      )}
    </>
  );
}

function BenchSection({
  bench,
  columnHeaders,
  onDropToBench,
  battingOrder,
  simEvents,
  simBaserunningEvents,
  simFieldingEvents,
  lineupEditsDisabled = false,
  onLoadOtherPlayers,
  isLoadingOtherPlayers = false,
  showLoadOtherPlayers = false,
  otherPlayers = [],
  otherSimEvents,
  otherSimBaserunningEvents,
  otherSimFieldingEvents,
}: BenchSectionProps) {
  const filteredBenchWithIndex = bench
    .map((player, originalIdx) => ({ player, originalIdx }))
    .filter(({ player }) => {
      const level = player.level?.toLowerCase();
      const status = player.status?.toLowerCase();
      return level === 'mlb' && status === 'active';
    });

  const sortedOtherPlayers = [...otherPlayers].sort((a, b) => {
    const aLast = (a.lastName || '').toLowerCase();
    const bLast = (b.lastName || '').toLowerCase();
    if (aLast < bLast) return -1;
    if (aLast > bLast) return 1;
    const aFirst = (a.firstName || '').toLowerCase();
    const bFirst = (b.firstName || '').toLowerCase();
    return aFirst.localeCompare(bFirst);
  });

  // Sort bench by lastName, then firstName, but keep track of original index
  const sortedBench = filteredBenchWithIndex
    .sort((a, b) => {
      const aLast = (a.player.lastName || '').toLowerCase();
      const bLast = (b.player.lastName || '').toLowerCase();
      if (aLast < bLast) return -1;
      if (aLast > bLast) return 1;
      const aFirst = (a.player.firstName || '').toLowerCase();
      const bFirst = (b.player.firstName || '').toLowerCase();
      return aFirst.localeCompare(bFirst);
    });

  const [{ isOver, canDrop }, benchDrop] = useDrop<BatterDnDItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: ItemTypes.BATTER,
    drop: (item: BatterDnDItem) => {
      onDropToBench(item.idx);
    },
    canDrop: (item) => {
      if (lineupEditsDisabled) return false;
      // Don't allow dropping pitchers to bench
      const player = battingOrder[item.idx];
      return player?.position !== 'P';
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Box flex="1">
      <Box
        ref={(node) => { benchDrop(node as any); }}
        style={{
          background: isOver ? (canDrop ? '#2d3748' : '#4A1C1C') : undefined,
          border: isOver ? `2px solid ${canDrop ? '#4299E1' : '#E53E3E'}` : '2px solid transparent',
          borderRadius: 'md',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="bold">Bench</Text>
        </Flex>
        <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
          <Thead bg={darkTableBg}>
            <Tr>
              <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
              <Th color={darkText} fontSize="sm" px={2} py={1}>POS</Th>
              {columnHeaders.slice(1).map((col: string) => (
                <Th key={col} color={darkText} textAlign="center" fontSize="sm" px={2} py={1} whiteSpace="nowrap">{batterStatColumnHeaderNode(col)}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedBench.map(({ player, originalIdx }) => (
              <BenchRow key={originalIdx} player={player} benchIdx={originalIdx} columnHeaders={columnHeaders} simEvents={simEvents} simBaserunningEvents={simBaserunningEvents} simFieldingEvents={simFieldingEvents} lineupEditsDisabled={lineupEditsDisabled} />
            ))}
          </Tbody>
        </Table>
      </Box>
      {showLoadOtherPlayers && (
        <Flex justify="center" mt={4}>
          <Button
            colorScheme="blue"
            variant="outline"
            onClick={onLoadOtherPlayers}
            isLoading={isLoadingOtherPlayers}
          >
            Show Minor Leaguers
          </Button>
        </Flex>
      )}
      {sortedOtherPlayers.length > 0 && (
        <Box mt={4}>
          <Text fontWeight="bold" mb={2}>Minor Leaguers</Text>
          <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
            <Thead bg={darkTableBg}>
              <Tr>
                <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
                <Th color={darkText} fontSize="sm" px={2} py={1}>POS</Th>
                {columnHeaders.slice(1).map((col: string) => (
                  <Th key={col} color={darkText} textAlign="center" fontSize="sm" px={2} py={1} whiteSpace="nowrap">{batterStatColumnHeaderNode(col)}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {sortedOtherPlayers.map((player) => (
                <OtherPlayerRow
                  key={player.roster_player_id ?? `${player.firstName}-${player.lastName}`}
                  player={player}
                  columnHeaders={columnHeaders}
                  simEvents={otherSimEvents}
                  simBaserunningEvents={otherSimBaserunningEvents}
                  simFieldingEvents={otherSimFieldingEvents}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

function PositionSelectPopover({ value, onChange, options, isDisabled, player }: { value: string | undefined; onChange: (val: string) => void; options: string[]; isDisabled?: boolean; player?: any }) {
  const { isOpen, onOpen, onClose } = useDisclosure();


  // Group positions by category
  // deal with LRF
  const primaries: string[] = [];
  if (Array.isArray(player?.primaryPosition)) {
    player.primaryPosition.forEach((pos: string) => {
      if (pos.includes('LRF')) {
        primaries.push('LF', 'RF');
      } else if (pos !== 'OF') {
        primaries.push(pos);
      } else {
        player.secondaryPosition.push('LF', 'CF', 'RF');
      }
    });
  }

  const primaryPositions = primaries;
  const secondaryPositions = Array.isArray(player?.secondaryPosition) ? player.secondaryPosition : [];
  const tertiaryPositions = Array.isArray(player?.tertiaryPosition) ? player.tertiaryPosition : [];
  const allDefensivePositions = [...primaryPositions, ...secondaryPositions, ...tertiaryPositions];
  const otherPositions = options.filter(pos => !allDefensivePositions.includes(pos));

  // Determine position color class
  const getPositionColorClass = (position: string | undefined): string => {
    if (!position || position === 'DH') return 'position-primary';

    const expandCodes = (codes: string[]) => {
      const out: string[] = [];
      for (const raw of codes) {
        if (!raw) continue;
        const token = raw.split(',')[0].trim().toUpperCase();
        if (token === 'C') {
          out.push('CA');
        } else if (token === 'LRF') {
          // Corner outfielder counts for both LF and RF
          out.push('LF', 'RF');
        } else if (token === 'IF') {
          // Infield utility counts for all four infield spots
          out.push('1B', '2B', '3B', 'SS');
        } else if (token === 'CF') {
          out.push('CF', 'LF', 'RF');
        } else if (token === 'SS') {
          out.push('SS', '2B', '3B');
        } else {
          out.push(token);
        }
      }
      return out;
    };

    const valueRaw = position.split(',')[0].trim().toUpperCase();
    const valueToken = valueRaw === 'C' ? 'CA' : valueRaw;
    const primarySet = new Set(expandCodes(primaryPositions));
    const secondarySet = new Set(expandCodes(secondaryPositions));
    const tertiarySet = new Set(expandCodes(tertiaryPositions));

    if (primarySet.has(valueToken)) return 'position-primary';
    if (secondarySet.has(valueToken)) return 'position-secondary';
    if (tertiarySet.has(valueToken)) return 'position-tertiary';
    return 'position-other';
  };

  const renderPositionButton = (pos: string, label?: string) => (
    <Button
      key={pos}
      size="xs"
      variant={pos === value ? 'solid' : 'ghost'}
      colorScheme={pos === value ? 'blue' : undefined}
      fontSize="sm"
      borderRadius="md"
      justifyContent="flex-start"
      onClick={() => {
        onChange(pos);
        onClose();
      }}
    >
      {label || pos}
    </Button>
  );

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Button
          size="xs"
          variant="ghost"
          minW="2.5em"
          px={1}
          py={0}
          fontSize="sm"
          onClick={onOpen}
          isDisabled={isDisabled}
          rightIcon={<ChevronDownIcon />}
          className={getPositionColorClass(value)}
        >
          {value || '-'}
        </Button>
      </PopoverTrigger>
      <PopoverContent w="auto" minW="0" maxW="10em" p={0} borderRadius="md" bg={darkTableBg}>
        <PopoverArrow />
        <PopoverBody p={1}>
          <VStack align="stretch" spacing={0}>
            {/* Primary Positions */}
            {primaryPositions.length > 0 && (
              <>
                <Text fontSize="xx-small" color="green.300" fontWeight="bold" px={2} py={1}>
                  Primary
                </Text>
                {primaryPositions.map((pos: string) => renderPositionButton(pos))}
                {(secondaryPositions.length > 0 || tertiaryPositions.length > 0 || otherPositions.length > 0) && (
                  <Divider my={1} />
                )}
              </>
            )}

            {/* Secondary/Tertiary Positions */}
            {(secondaryPositions.length > 0 || tertiaryPositions.length > 0) && (
              <>
                <Text fontSize="xx-small" color="gray.400" fontWeight="bold" px={2} py={1}>
                  Secondary
                </Text>
                {secondaryPositions.map((pos: string) => renderPositionButton(pos))}
                {tertiaryPositions.map((pos: string) => renderPositionButton(pos))}
                {otherPositions.length > 0 && <Divider my={1} />}
              </>
            )}

            {/* Other Positions */}
            {otherPositions.length > 0 && (
              <>
                <Text fontSize="xx-small" color="gray.500" fontWeight="bold" px={2} py={1}>
                  Other
                </Text>
                {otherPositions.map((pos) => renderPositionButton(pos))}
              </>
            )}
          </VStack>
          <Divider my={2} />
          <Button size="xs" variant="ghost" w="100%" onClick={onClose} fontSize="sm">
            ✕ Close
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>

  );
}

function BattingOrderRow({
  slot = {},
  idx,
  moveBatter,
  onDropFromBench,
  positions,
  columnHeaders,
  editingPosIdx,
  setEditingPosIdx,
  setBattingOrder,
  pitchers,
  battingOrder,
  commitLineupUpdate,
  simEvents,
  simBaserunningEvents,
  simFieldingEvents,
  lineupEditsDisabled = false
}: BattingOrderRowProps) {
  const posList = positions ?? ['P', 'CA', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];

  // Accept both BATTER and BENCH types for drop
  const [{ isOver, canDrop }, drop] = useDrop<any, void, { isOver: boolean; canDrop: boolean }>({
    accept: [ItemTypes.BATTER, ItemTypes.BENCH],
    drop: (item: any) => {
      if (item.type === ItemTypes.BATTER) {
        moveBatter(item.idx, idx);
      } else if (item.type === ItemTypes.BENCH) {
        onDropFromBench(item.benchIdx, idx);
      }
    },
    canDrop: () => !lineupEditsDisabled,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [{ isDragging }, drag] = useDrag<BatterDnDItem, void, { isDragging: boolean }>({
    type: ItemTypes.BATTER,
    item: { idx, type: ItemTypes.BATTER },
    canDrag: !!slot?.name && !lineupEditsDisabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Helper for "med" size name: first initial + last name
  const getMedName = (slot: any) => {
    if (slot?.first_name && slot?.last_name) {
      return `${slot.first_name.charAt(0)}. ${slot.last_name}`;
    }
    if (slot?.full_name) return slot.full_name;
    if (slot?.name) return slot.name;
    return '';
  };

  const statusUntil = getStatusUntilValue(slot);
  const isStatusUntil = statusUntil != null && slot?.position !== 'DH';

  const handleLockClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (lineupEditsDisabled || !slot?.roster_player_id) return;
    const previousOrder = [...battingOrder];
    const newOrder = [...battingOrder];
    newOrder[idx] = { ...newOrder[idx], lock: !(slot.lock ?? false) };
    setBattingOrder(newOrder);
    const entries = newOrder
      .map((s, i) => {
        if (!s || !s.roster_player_id || !s.position) return undefined;
        return { roster_player_id: s.roster_player_id, position: s.position, batting_order: i, lock: s.lock ?? false };
      })
      .filter((e): e is NonNullable<typeof e> => e != null);
    commitLineupUpdate(entries, previousOrder);
  };

  return (
    <Tr
      ref={(node) => { drop(node as any); }}
      fontStyle={isStatusUntil ? 'italic' : undefined}
      sx={isStatusUntil ? { td: { color: 'gray.400' } } : undefined}
      style={{
        background: isOver && canDrop ? '#2d3748' : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: lineupEditsDisabled ? 'default' : 'pointer'
      }}
    >
      <Td fontSize="sm" px={2} py={1}>{idx + 1}</Td>
      <Td
        fontSize="sm"
        px={1}
        py={1}
        w={8}
        textAlign="center"
        onClick={handleLockClick}
        cursor={slot?.roster_player_id && !lineupEditsDisabled ? 'pointer' : 'default'}
        title={slot?.roster_player_id ? (slot.lock ? 'Locked (no PH)' : 'Unlocked (PH allowed)') : ''}
      >
        {slot?.roster_player_id && (
          <Icon
            as={LockIcon}
            boxSize={slot.lock ? 3.5 : 3}
            color={slot.lock ? 'white' : 'gray.500'}
            opacity={slot.lock ? 1 : 0.6}
          />
        )}
      </Td>
      <Td ref={(node) => { drag(node as any); }} style={{ cursor: slot?.name && !lineupEditsDisabled ? 'move' : 'default' }} fontSize="sm" px={2} py={1}>
        {slot?.name || slot?.firstName || slot?.lastName ? (
          <Box>
            {getMedName(slot)}
            {isStatusUntil && (
              <Text as="i" fontSize="xs" color="gray.400" display="block">
                Avail: {formatAvailMD(statusUntil as string)}
              </Text>
            )}
          </Box>
        ) : (
          <Text color={"gray.600"}>no player</Text>
        )}
      </Td>
      <Td fontSize="sm" px={2} py={1}>
        {slot?.name && (
        <PositionSelectPopover
          value={slot?.position}
          player={slot}
          onChange={val => {
            const previousOrder = [...battingOrder];
            const newOrder = [...battingOrder];
            newOrder[idx] = { ...newOrder[idx], position: val };
            setBattingOrder(newOrder);
            // Immediately save to backend
            const entries = newOrder
              .map((slot, index) => {
                if (!slot || !slot.roster_player_id || !slot.position) return undefined;
                return {
                  ...slot,
                  batting_order: index
                };
              })
              .filter((entry) => entry != null);
            commitLineupUpdate(entries, previousOrder);
          }}
          options={posList}
          isDisabled={!slot?.name || lineupEditsDisabled}
        />
        )}
      </Td>
      {columnHeaders.slice(1).map((col: string, i: number) => {
        const mlbId = (slot as any)?.mlb_id;
        const rosterPlayerId = (slot as any)?.roster_player_id;
        const value = getBatterStatColumnValue(
          col,
          rosterPlayerId,
          mlbId,
          simEvents,
          simBaserunningEvents,
          simFieldingEvents,
          slot
        );
        return (
          <Td key={col + i} textAlign="center" color={darkSecondary} fontSize="sm" px={2} py={1}>{value}</Td>
        );
      })}
    </Tr>
  );
}

function OtherPlayerRow({
  player,
  columnHeaders,
  simEvents,
  simBaserunningEvents,
  simFieldingEvents,
}: {
  player: PlayerType;
  columnHeaders: string[];
  simEvents?: SimEvent[];
  simBaserunningEvents?: SimBaserunningEventRow[];
  simFieldingEvents?: SimFieldingEventRow[];
}) {
  const p = player as any;

  return (
    <Tr>
      <Td fontSize="sm" px={2} py={1}>
        {getPlayerShortName(p)}
      </Td>
      <Td fontSize="sm" px={2} py={1}>
        {renderBenchPositionCell(p)}
      </Td>
      {columnHeaders.slice(1).map((col: string, i: number) => {
        const mlbId = p?.mlb_id;
        const rosterPlayerId = p?.roster_player_id ?? p?.id;
        const value = getBatterStatColumnValue(
          col,
          rosterPlayerId,
          mlbId,
          simEvents,
          simBaserunningEvents,
          simFieldingEvents,
          player
        );
        return (
          <Td key={col + i} textAlign="center" color={darkSecondary} fontSize="sm" px={2} py={1}>{value}</Td>
        );
      })}
    </Tr>
  );
}

function BenchRow({ player, benchIdx, columnHeaders, simEvents, simBaserunningEvents, simFieldingEvents, lineupEditsDisabled = false }: BenchRowProps & { columnHeaders: string[] }) {
  const p = player as any; // for backend fields not in PlayerType
  const [{ isDragging }, drag] = useDrag<BenchDnDItem, void, { isDragging: boolean }>({
    type: ItemTypes.BENCH,
    item: { benchIdx, type: ItemTypes.BENCH },
    canDrag: !lineupEditsDisabled && !!(p?.name || p?.full_name || p?.first_name || p?.last_name),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  // Helper for F. LastName (matching batting order format)
  const getBenchName = getPlayerShortName;

  const statusUntil = getStatusUntilValue(p);
  const isStatusUntil = statusUntil != null;

  return (
    <Tr
      ref={(node) => { drag(node as any); }}
      fontStyle={isStatusUntil ? 'italic' : undefined}
      sx={isStatusUntil ? { td: { color: 'gray.400' } } : undefined}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: !lineupEditsDisabled && (p?.name || p?.full_name || p?.first_name) ? 'move' : 'default'
      }}
    >
      <Td fontSize="sm" px={2} py={1}>
        <Box>
          {getBenchName(p)}
          {isStatusUntil && (
            <Text as="i" fontSize="xs" color="gray.400" display="block">
              Avail: {formatAvailMD(statusUntil as string)}
            </Text>
          )}
        </Box>
      </Td>
      <Td fontSize="sm" px={2} py={1}>
        {renderBenchPositionCell(p)}
      </Td>
      {columnHeaders.slice(1).map((col: string, i: number) => {
        const mlbId = (player as any)?.mlb_id;
        const rosterPlayerId = (player as any)?.roster_player_id ?? (player as any)?.id;
        const value = getBatterStatColumnValue(
          col,
          rosterPlayerId,
          mlbId,
          simEvents,
          simBaserunningEvents,
          simFieldingEvents,
          player
        );
        return (
          <Td key={col + i} textAlign="center" color={darkSecondary} fontSize="sm" px={2} py={1}>{value}</Td>
        );
      })}
    </Tr>
  );
}

function getMedName(slot: any) {
  if (slot?.first_name && slot?.last_name) {
    return `${slot.first_name.charAt(0)}. ${slot.last_name}`;
  }
  if (slot?.full_name) return slot.full_name;
  if (slot?.name) return slot.name;
  return '';
}

function PitcherRow({ pitcher, idx, movePitcher, columnHeaders, lineupEditsDisabled = false }: { pitcher: PlayerType; idx: number; movePitcher: (fromIdx: number, toIdx: number) => void; columnHeaders: string[]; lineupEditsDisabled?: boolean }) {
  const statusUntil = getStatusUntilValue(pitcher);
  const isStatusUntil = !!statusUntil;

  const [{ isOver, canDrop }, drop] = useDrop<PitcherDnDItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: ItemTypes.PITCHER,
    drop: (item: PitcherDnDItem) => {
      movePitcher(item.idx, idx);
    },
    canDrop: () => !lineupEditsDisabled,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [{ isDragging }, drag] = useDrag<PitcherDnDItem, void, { isDragging: boolean }>({
    type: ItemTypes.PITCHER,
    item: { idx, type: ItemTypes.PITCHER },
    canDrag: !lineupEditsDisabled && !!(pitcher?.name || pitcher?.full_name || pitcher?.first_name),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Tr
      ref={(node) => { drag(drop(node as any)); }}
      fontStyle={isStatusUntil ? 'italic' : undefined}
      sx={isStatusUntil ? { td: { color: 'gray.400' } } : undefined}
      style={{
        background: isOver && canDrop ? '#2d3748' : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: !lineupEditsDisabled && (pitcher?.name || pitcher?.full_name || pitcher?.first_name) ? 'move' : 'default'
      }}
    >
      <Td fontSize="sm" px={2} py={1} color={isStatusUntil ? 'gray.400' : darkText} fontWeight="bold">{idx + 1}</Td>
      <Td fontSize="sm" px={2} py={1}>
        <Box>
          {getMedName(pitcher)}
          {isStatusUntil && (
            <Text as="i" fontSize="xs" color="gray.400" display="block">
              Avail: {formatAvailMD(statusUntil as string)}
            </Text>
          )}
        </Box>
      </Td>
      {columnHeaders.map((col: string, i: number) => {
        const isRestColumn = !col;
        if (isRestColumn) {
          return (
            <Td key={`rest-${i}`} textAlign="center" color={darkSecondary} fontSize="sm" px={2} py={1}></Td>
          );
        }
        const ipNum = Number((pitcher as any)?.stats?.IP ?? 0);
        const hasIp = Number.isFinite(ipNum) && ipNum > 0;
        let display = '-';
        if (col === 'Avail. IP') {
          display = hasIp ? String((pitcher as any).stats?.IP ?? '-') : '-';
        } else if (col === 'LW') {
          display = hasIp ? String((pitcher as any).stats?.LW ?? '-') : '-';
        }
        return (
          <Td key={col + i} textAlign="center" color={darkSecondary} fontSize="sm" px={2} py={1}>
            {display}
          </Td>
        );
      })}
    </Tr>
  );
}

function PitchersSection({
  pitchers,
  onDropToPitchers,
  battingOrder,
  movePitcher,
  onSwitchToPitchersTab,
  lineupEditsDisabled = false
}: PitchersSectionProps) {
  const filteredPitchers = pitchers.filter(pitcher => {
    const level = pitcher.level?.toLowerCase();
    const status = pitcher.status?.toLowerCase();
    return level === 'mlb' && status === 'active';
  });

  const [{ isOver, canDrop }, pitchersDrop] = useDrop<BatterDnDItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: ItemTypes.BATTER,
    drop: (item: BatterDnDItem) => {
      onDropToPitchers(item.idx);
    },
    canDrop: (item) => {
      if (lineupEditsDisabled) return false;
      const player = battingOrder[item.idx];
      return player?.position === 'P';
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Box
      mb={4}
      ref={(node) => { pitchersDrop(node as any); }}
      style={{
        background: isOver ? (canDrop ? '#2d3748' : '#4A1C1C') : undefined,
        border: isOver ? `2px solid ${canDrop ? '#4299E1' : '#E53E3E'}` : '2px solid transparent',
        borderRadius: 'md',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold">Starting Pitchers</Text>
        {onSwitchToPitchersTab && (
          <Link
            as="button"
            type="button"
            onClick={onSwitchToPitchersTab}
            color="blue.400"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            Manage Pitchers
          </Link>
        )}
      </Flex>
      <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
        <Thead bg={darkTableBg}>
          <Tr>
            <Th color={darkText} fontSize="sm" px={2} py={1}>#</Th>
            <Th color={darkText} fontSize="sm" px={2} py={1}>Player</Th>
            {PITCHER_COLUMN_HEADERS.map((col, i) => (
              <Th key={col ? col : `rest-${i}`} color={darkText} textAlign="center" fontSize="sm" px={2} py={1}>{col}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {filteredPitchers.map((p, idx) => (
            <PitcherRow key={idx} pitcher={p} idx={idx} movePitcher={movePitcher} columnHeaders={PITCHER_COLUMN_HEADERS} lineupEditsDisabled={lineupEditsDisabled} />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

interface LineupManagementProps {
  teamId: string;
  onSwitchToPitchersTab?: () => void;
}

const LineupManagement: React.FC<LineupManagementProps> = ({ teamId, onSwitchToPitchersTab }) => {
  const [tabIndex, setTabIndex] = useState(0); // 0 = DH, 1 = 9 man
  const [editingPosIdx, setEditingPosIdx] = useState<number | null>(null);
  const toast = useToast();
  const modernToast = useModernToast();
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const myTeam = useUserStore((s) => s.user?.user_data?.myTeam);
  const isAdminUser = isAdmin();
  const dhSetting = myLeague?.dh?.toLowerCase();
  const lineupLockedByLeague = isLineupEditingDisabled(myLeague);
  const adminEditingOtherTeam =
    isAdminUser &&
    myTeam != null &&
    Boolean(teamId) &&
    Number(teamId) !== Number(myTeam);
  const lineupEditsDisabled = lineupLockedByLeague || adminEditingOtherTeam;
  const isDHOnly = dhSetting === 'on';
  const isDHOff = dhSetting === 'off';
  const isDHMixed = dhSetting === 'mixed';

  const {
    lineup: apiBattingOrder,
    bench,
    simEvents,
    locked: lineupsLocked,
    simBaserunningEvents,
    simFieldingEvents,
    isLoading: isLineupLoading,
    isUpdating: isLineupUpdating,
    error: lineupError,
    updateLineup,
    refreshLineup
  } = useTeamLineup(teamId || '', tabIndex === 0 ? 'DH' : 'NINE_MAN');

  const showLockedLineupBanner = lineupLockedByLeague || lineupsLocked;
  const dndDisabled = lineupEditsDisabled || lineupsLocked;

  const {
    rotation,
    simEvents: rotationSimEvents,
    isLoading: isRotationLoading,
    isUpdating: isRotationUpdating,
    error: rotationError,
    updateRotation,
    refreshRotation
  } = useTeamPitchingRotation(teamId || '');

  // Safely extract starters from rotation
  const starters = (rotation && typeof rotation === 'object' && 'starters' in rotation && Array.isArray(rotation.starters)) ? rotation.starters : [];

  const pitcherSimMap = useMemo(
    () => buildPitcherSimMap(rotationSimEvents || []),
    [rotationSimEvents]
  );
  const startersWithPitcherStats = useMemo(
    () => starters.map((p: PlayerType) => attachSimStats(p, pitcherSimMap)),
    [starters, pitcherSimMap]
  );

  // Local batting order state for instant UI feedback
  const [battingOrder, setBattingOrder] = useState<PlayerTypeWithRosterId[]>([]);
  // Local bench state for instant UI feedback (kept in sync with API)
  const [benchState, setBenchState] = useState<PlayerTypeWithRosterId[]>([]);
  const [otherPlayersState, setOtherPlayersState] = useState<PlayerTypeWithRosterId[]>([]);
  const [otherPlayersLoaded, setOtherPlayersLoaded] = useState(false);
  const [isLoadingOtherPlayers, setIsLoadingOtherPlayers] = useState(false);
  const [otherSimEvents, setOtherSimEvents] = useState<SimEvent[]>([]);
  const [otherSimBaserunningEvents, setOtherSimBaserunningEvents] = useState<SimBaserunningEventRow[]>([]);
  const [otherSimFieldingEvents, setOtherSimFieldingEvents] = useState<SimFieldingEventRow[]>([]);

  const getApiErrorMessage = useCallback((err: unknown): string => {
    if (err && typeof err === 'object') {
      const maybeErr = err as { message?: unknown; error?: unknown };
      if (typeof maybeErr.message === 'string' && maybeErr.message.trim()) {
        return maybeErr.message;
      }
      if (typeof maybeErr.error === 'string' && maybeErr.error.trim()) {
        return maybeErr.error;
      }
    }
    return 'Failed to update lineup';
  }, []);

  const commitRotationUpdate = useCallback(
    (entries: PlayerType[]) => {
      updateRotation(entries, {
        onError: (err: unknown) => {
          toast({
            title: 'Pitching rotation update failed',
            description: getApiErrorMessage(err),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          refreshRotation();
        },
      });
    },
    [getApiErrorMessage, refreshRotation, toast, updateRotation]
  );

  const commitLineupUpdate = useCallback(
    (
      entries: PlayerTypeWithRosterId[],
      previousBattingOrder: PlayerTypeWithRosterId[],
      previousBenchState?: PlayerTypeWithRosterId[]
    ) => {
      updateLineup(entries, {
        onError: (err: unknown) => {
          setBattingOrder(previousBattingOrder);
          if (previousBenchState) {
            setBenchState(previousBenchState);
          }
          toast({
            title: 'Lineup update failed',
            description: getApiErrorMessage(err),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          refreshLineup();
        },
      });
    },
    [getApiErrorMessage, refreshLineup, toast, updateLineup]
  );

  // Sync local state with API data and ensure we always have 9 slots
  useEffect(() => {
    if (apiBattingOrder) {
      // Create an array of 9 slots, filling in with empty objects for missing slots
      const newOrder = Array.from({ length: 9 }, (_, i) => apiBattingOrder[i] || {});
      setBattingOrder(newOrder);
    }
  }, [apiBattingOrder]);

  useEffect(() => {
    setOtherPlayersLoaded(false);
    setOtherPlayersState([]);
    setOtherSimEvents([]);
    setOtherSimBaserunningEvents([]);
    setOtherSimFieldingEvents([]);
  }, [teamId]);

  useEffect(() => {
    if (Array.isArray(bench)) {
      setBenchState(bench as PlayerTypeWithRosterId[]);
    }
  }, [bench]);

  const onLoadOtherPlayers = useCallback(async () => {
    if (!teamId) return;
    setIsLoadingOtherPlayers(true);
    try {
      const data = await fetchTeamExtendedBench(teamId);
      const extended = (data.extendedBench || []) as PlayerTypeWithRosterId[];
      if (extended.length === 0) {
        modernToast.info('No minor leaguers', 'No minor-league players with available PA were found.');
        setOtherPlayersLoaded(true);
        return;
      }
      setOtherPlayersState(extended);
      setOtherSimEvents(data.simEvents || []);
      setOtherSimBaserunningEvents(data.simBaserunningEvents || []);
      setOtherSimFieldingEvents(data.simFieldingEvents || []);
      setOtherPlayersLoaded(true);
    } catch (err: unknown) {
      modernToast.error('Failed to load minor leaguers', getApiErrorMessage(err));
    } finally {
      setIsLoadingOtherPlayers(false);
    }
  }, [teamId, modernToast, getApiErrorMessage]);

  // Move batter within batting order
  const moveBatter = useCallback((fromIdx: number, toIdx: number) => {
    console.log('moveBatter called:', { fromIdx, toIdx, battingOrder });

    // Ensure we have valid indices
    if (fromIdx < 0 || toIdx < 0 || fromIdx >= 9 || toIdx >= 9) {
      console.log('Invalid indices:', { fromIdx, toIdx });
      return;
    }

    // Update local state first for immediate UI feedback
    const previousOrder = [...battingOrder];
    const newOrder = [...battingOrder];
    const fromSlot = newOrder[fromIdx] || {};
    const toSlot = newOrder[toIdx] || {};

    console.log('Slots:', { fromSlot, toSlot });

    if (!toSlot.name) {
      // Move to empty slot
      newOrder[toIdx] = { ...fromSlot };
      newOrder[fromIdx] = {};
    } else {
      // Swap
      newOrder[toIdx] = { ...fromSlot };
      newOrder[fromIdx] = { ...toSlot };
    }

    console.log('New order:', newOrder);

    // Update local state immediately
    setBattingOrder(newOrder);

    // Prepare and send update to backend
    const entries = newOrder
      .map((slot, index) => {
        if (!slot || !slot.roster_player_id) return null;
        return {
          roster_player_id: slot.roster_player_id,
          position: slot.position,
          batting_order: index,
          lock: slot.lock ?? false
        } as PlayerTypeWithRosterId;
      })
      .filter((entry): entry is PlayerTypeWithRosterId => entry !== null);

    console.log('Sending to backend:', entries);

    // Send update to backend
    commitLineupUpdate(entries, previousOrder);
  }, [battingOrder, commitLineupUpdate]);

  // Drop from bench to batting order
  const onDropFromBench = useCallback((benchIdx: number, toIdx: number) => {
    if (!battingOrder || !benchState) return;

    const benchPlayer = benchState[benchIdx];
    if (!benchPlayer || !benchPlayer.roster_player_id) {
      console.error('Invalid bench player at index:', benchIdx, benchPlayer);
      return;
    }
    const benchPlayerAny = benchPlayer as any;

    const previousOrder = [...battingOrder];
    const previousBench = [...benchState];
    const newOrder = [...battingOrder];
    const displaced = newOrder[toIdx];

    // Get available positions based on lineup type
    const availablePositions = tabIndex === 0 ? positionsDH : positions;

    // Get currently taken positions (excluding the slot we're dropping into)
    const takenPositions = new Set(
      newOrder
        .map((slot, idx) => idx === toIdx ? null : slot?.position)
        .filter(Boolean)
    );

    // Helper to identify pitcher roles
    const isPitcherPos = (pos: string) => {
      const norm = normalizePosition(pos);
      return norm === 'P' || norm === 'SP' || norm === 'RP' || norm === 'CP' || norm === 'CL';
    };

    // Get player's defensive positions
    const primaryPositions = Array.isArray(benchPlayerAny?.primaryPosition) ? benchPlayerAny.primaryPosition : [];
    const secondaryPositions = Array.isArray(benchPlayerAny?.secondaryPosition) ? benchPlayerAny.secondaryPosition : [];
    const tertiaryPositions = Array.isArray(benchPlayerAny?.tertiaryPosition) ? benchPlayerAny.tertiaryPosition : [];

    // If the player has any non-pitcher positions, prefer those over pitcher roles
    const allPositions = [...primaryPositions, ...secondaryPositions, ...tertiaryPositions].filter(Boolean) as string[];
    const hasNonPitcherOption = allPositions.some((p) => !isPitcherPos(p));

    // Helper to find first available position from a list
    const findFirstAvailable = (positionList: string[]) => {
      for (const raw of positionList) {
        if (!raw) continue;
        const token = raw.split(',')[0].trim().toUpperCase();

        // Special handling: LRF can fill LF or RF, prefer an open corner
        if (token === 'LRF') {
          for (const corner of ['LF', 'RF']) {
            if (takenPositions.has(corner)) continue;
            if (hasNonPitcherOption && isPitcherPos(corner)) continue;
            return corner;
          }
          continue;
        }

        // Special handling: IF can fill 1B, 2B, 3B, SS – prefer first open infield spot
        if (token === 'IF') {
          for (const infield of ['1B', '2B', '3B', 'SS']) {
            if (takenPositions.has(infield)) continue;
            if (hasNonPitcherOption && isPitcherPos(infield)) continue;
            return infield;
          }
          continue;
        }

        if (takenPositions.has(token)) continue;
        if (hasNonPitcherOption && isPitcherPos(token)) continue;
        return token;
      }
      return undefined;
    };

    // Try to find an available position in order: primary > secondary > tertiary
    let selectedPosition = findFirstAvailable(primaryPositions) ||
      findFirstAvailable(secondaryPositions) ||
      findFirstAvailable(tertiaryPositions);

    // If no defensive position available, find first available unfilled defensive position (or DH)
    if (!selectedPosition) {
      selectedPosition = findFirstAvailable(availablePositions) || 'DH';
    }

    newOrder[toIdx] = {
      ...benchPlayer,
      position: selectedPosition,
      name:
        benchPlayer.name ||
        benchPlayer.full_name ||
        ((benchPlayer.first_name && benchPlayer.last_name)
          ? `${benchPlayer.first_name} ${benchPlayer.last_name}`
          : undefined)
    };

    setBattingOrder(newOrder); // update local state for immediate UI feedback
    // Optimistically remove from bench; if we displaced a lineup player, add them to bench
    setBenchState(prev => {
      const next = [...prev];
      next.splice(benchIdx, 1);
      if (displaced && (displaced as any)?.roster_player_id) {
        next.push(displaced as PlayerTypeWithRosterId);
      }
      return next;
    });

    // Prepare and send update to backend
    const entries = newOrder
      .map((slot, index) => {
        if (!slot || !slot.roster_player_id || !slot.position) return undefined;
        return {
          roster_player_id: slot.roster_player_id,
          position: slot.position,
          batting_order: index,
          lock: slot.lock ?? false
        };
      })
      .filter((entry) => entry != null);

    commitLineupUpdate(entries, previousOrder, previousBench);
  }, [battingOrder, benchState, commitLineupUpdate, tabIndex]);

  // Drop from batting order to bench
  const onDropToBench = useCallback((fromIdx: number) => {
    if (!battingOrder) return;

    const previousOrder = [...battingOrder];
    const previousBench = [...benchState];
    const newOrder = [...battingOrder];
    const removed = newOrder[fromIdx];
    newOrder[fromIdx] = {};
    setBattingOrder(newOrder); // update local state for immediate UI feedback
    if (removed && (removed as any)?.roster_player_id) {
      setBenchState(prev => [...prev, removed]);
    }

    // Prepare and send update to backend
    const entries = newOrder
      .map((slot, index) => {
        if (!slot || !slot.roster_player_id || !slot.position) return undefined;
        return {
          roster_player_id: slot.roster_player_id,
          position: slot.position,
          batting_order: index,
          lock: slot.lock ?? false
        };
      })
      .filter((entry) => entry != null);

    commitLineupUpdate(entries, previousOrder, previousBench);
  }, [battingOrder, benchState, commitLineupUpdate]);

  // Move pitcher within pitchers list
  const movePitcher = useCallback((fromIdx: number, toIdx: number) => {
    if (!starters || !Array.isArray(starters)) return;
    const newOrder = [...starters];
    const temp = { ...newOrder[toIdx] };
    newOrder[toIdx] = { ...newOrder[fromIdx] };
    newOrder[fromIdx] = temp;
    commitRotationUpdate(newOrder);
  }, [commitRotationUpdate, starters]);

  // Drop from batting order to pitchers list
  const onDropToPitchers = useCallback((fromIdx: number) => {
    if (!battingOrder || !starters || !Array.isArray(starters)) return;
    const player = battingOrder[fromIdx];
    if (player && player.name && player.position === 'P') {
      const newPitchers = [...starters, player];
      commitRotationUpdate(newPitchers);
      const previousOrder = [...battingOrder];
      const newOrder = [...battingOrder];
      newOrder[fromIdx] = {};
      setBattingOrder(newOrder);
      const entries = newOrder
        .map((slot, index) => {
          if (!slot || !slot.roster_player_id || !slot.position) return undefined;
          return {
            roster_player_id: slot.roster_player_id,
            position: slot.position,
            batting_order: index,
            lock: (slot as any).lock ?? false
          };
        })
        .filter((entry) => entry != null);
      commitLineupUpdate(entries, previousOrder);
    }
  }, [battingOrder, commitLineupUpdate, commitRotationUpdate, starters]);

  // Column headers for batting order and bench
  const columnHeaders = [
    'Pos', 'Avail. PA', 'LW', 'BR', 'FLD', 'DEF', 'Con. Games'
  ].filter(col => col !== 'Con. Games');

  const normalizePosition = (pos: string | undefined) => {
    if (!pos) return '';
    // Use first token before comma and trim/uppercase to handle values like "SS, 3B"
    const token = pos.split(',')[0].trim().toUpperCase();
    if (token === 'C') return 'CA';
    if (token === 'LRF') return 'LF'; // treat corner OF as LF/RF; we normalize to LF for counting
    return token;
  };

  const getMissingPositions = (order: PlayerTypeWithRosterId[], requiredPositions: string[]) => {
    const present = order.map(slot => normalizePosition(slot.position)).filter(Boolean);
    return requiredPositions.filter(pos => !present.includes(pos));
  };

  const dhMissingPositions = getMissingPositions(battingOrder, positionsDH);
  const nineManMissingPositions = getMissingPositions(battingOrder, positions);

  const getDuplicatePositions = (order: PlayerTypeWithRosterId[], requiredPositions: string[]) => {
    const posCount: Record<string, number> = {};
    order.forEach(slot => {
      const norm = normalizePosition(slot.position);
      if (norm && requiredPositions.includes(norm)) {
        posCount[norm] = (posCount[norm] || 0) + 1;
      }
    });
    return Object.entries(posCount)
      .filter(([_, count]) => count > 1)
      .map(([pos]) => pos);
  };

  const dhDuplicatePositions = getDuplicatePositions(battingOrder, positionsDH);
  const nineManDuplicatePositions = getDuplicatePositions(battingOrder, positions);

  // Always show 9 slots in the batting order
  const battingOrderWithSlots = Array.from({ length: 9 }, (_, i) => battingOrder[i] || {});

  // Instead of putting mt={6} inside the gray panel, wrap the whole panel in a Box with mt={6} when isDHOnly is true
  if (isDHOnly) {
    return (
      <>
        <style>{positionColorStyles}</style>
        <Box mt={6}>
          <Box p={6} bg={darkBg} borderRadius="lg" color={darkText}>
            <LineupWarningBanner show={adminEditingOtherTeam} message="You do not have permission to edit this lineup" />
            <LineupWarningBanner show={showLockedLineupBanner} message="Lineups are currently locked." />
            <Text color="red.400" fontWeight="bold" mb={2}>
              {dhMissingPositions.length > 0 ? `Lineup incomplete. Position(s) missing: ${dhMissingPositions.join(' ')}` : ''}
            </Text>
            {dhDuplicatePositions.length > 0 && (
              <Text color="red.400" fontWeight="bold" mb={2}>
                {`There are multiple players at: ${dhDuplicatePositions.join(' ')}`}
              </Text>
            )}
            <Flex gap={8}>
              <Box flex="2">
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="bold">Batting order</Text>
                  <Flex align="center" gap={1} color={darkSecondary} fontSize="xs">
                    <Icon as={LockIcon} boxSize={3} />
                    <Text>= No PH for this batter</Text>
                  </Flex>
                </Flex>
                <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
                  <Thead bg={darkTableBg}>
                    <Tr>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>#</Th>
                      <Th color={darkText} fontSize="sm" px={1} py={1} w={8} textAlign="center">
                        <Icon as={LockIcon} boxSize={3} />
                      </Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>POS</Th>
                      {columnHeaders.slice(1).map((col: string) => (
                        <Th key={col} color={darkText} textAlign="center" fontSize="sm" px={2} py={1} whiteSpace="nowrap">{batterStatColumnHeaderNode(col)}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {battingOrderWithSlots.map((slot, idx) => (
                      <BattingOrderRow
                        key={idx}
                        slot={slot}
                        idx={idx}
                        moveBatter={moveBatter}
                        onDropFromBench={onDropFromBench}
                        positions={positionsDH}
                        columnHeaders={columnHeaders}
                        editingPosIdx={editingPosIdx}
                        setEditingPosIdx={setEditingPosIdx}
                        setBattingOrder={setBattingOrder}
                        pitchers={starters}
                        battingOrder={battingOrder}
                        commitLineupUpdate={commitLineupUpdate}
                        simEvents={simEvents}
                        simBaserunningEvents={simBaserunningEvents}
                        simFieldingEvents={simFieldingEvents}
                        lineupEditsDisabled={dndDisabled}
                      />
                    ))}
                  </Tbody>
                </Table>
                <DefColumnFootnote />
                <Box mt={4}>
                  {starters.length > 0 ? (
                    <>
                      <Divider my={4} borderColor="gray.700" />
                      <PitchersSection
                        pitchers={startersWithPitcherStats}
                        onDropToPitchers={onDropToPitchers}
                        battingOrder={battingOrder}
                        movePitcher={movePitcher}
                        onSwitchToPitchersTab={onSwitchToPitchersTab}
                        lineupEditsDisabled={dndDisabled}
                      />
                    </>
                  ) : (
                    <Box mt={2}>
                      <Text color={darkSecondary} mb={2}>No Starting Pitchers Available</Text>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={onSwitchToPitchersTab ?? (() => { window.location.href = '/roster?tab=Pitchers'; })}
                      >
                        Manage Your Pitchers
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              <BenchSection
                bench={benchState}
                columnHeaders={columnHeaders}
                onDropToBench={onDropToBench}
                battingOrder={battingOrder}
                simEvents={simEvents}
                simBaserunningEvents={simBaserunningEvents}
                simFieldingEvents={simFieldingEvents}
                lineupEditsDisabled={dndDisabled}
                onLoadOtherPlayers={onLoadOtherPlayers}
                isLoadingOtherPlayers={isLoadingOtherPlayers}
                showLoadOtherPlayers={!otherPlayersLoaded}
                otherPlayers={otherPlayersState}
                otherSimEvents={otherSimEvents}
                otherSimBaserunningEvents={otherSimBaserunningEvents}
                otherSimFieldingEvents={otherSimFieldingEvents}
              />
            </Flex>
          </Box>
        </Box>
      </>
    );
  }
  // Render the Tabs as before
  return (
    <>
      <style>{positionColorStyles}</style>
      <LineupWarningBanner show={adminEditingOtherTeam} message="You do not have permission to edit this lineup" />
      <LineupWarningBanner show={showLockedLineupBanner} message="Lineups are currently locked." />
      <Tabs variant="enclosed" colorScheme="gray" index={tabIndex} onChange={setTabIndex}>
        <TabList>
          <Tab>DH</Tab>
          <Tab>9 man</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* DH TabPanel content */}
            <Text color="red.400" fontWeight="bold" mb={2}>
              {dhMissingPositions.length > 0 ? `Lineup incomplete. Position(s) missing: ${dhMissingPositions.join(' ')}` : ''}
            </Text>
            {dhDuplicatePositions.length > 0 && (
              <Text color="red.400" fontWeight="bold" mb={2}>
                {`There are multiple players at: ${dhDuplicatePositions.join(' ')}`}
              </Text>
            )}
            <Flex gap={8}>
              {/* Left column: Batting Order + Pitchers */}
              <Box flex="2">
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="bold">Batting order</Text>
                  <Flex align="center" gap={1} color={darkSecondary} fontSize="xs">
                    <Icon as={LockIcon} boxSize={3} />
                    <Text>= No PH for this batter</Text>
                  </Flex>
                </Flex>
                <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
                  <Thead bg={darkTableBg}>
                    <Tr>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>#</Th>
                      <Th color={darkText} fontSize="sm" px={1} py={1} w={8} textAlign="center">
                        <Icon as={LockIcon} boxSize={3} />
                      </Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>POS</Th>
                      {columnHeaders.slice(1).map((col: string) => (
                        <Th key={col} color={darkText} textAlign="center" fontSize="sm" px={2} py={1} whiteSpace="nowrap">{batterStatColumnHeaderNode(col)}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {battingOrderWithSlots.map((slot, idx) => (
                      <BattingOrderRow
                        key={idx}
                        slot={slot}
                        idx={idx}
                        moveBatter={moveBatter}
                        onDropFromBench={onDropFromBench}
                        positions={positionsDH}
                        columnHeaders={columnHeaders}
                        editingPosIdx={editingPosIdx}
                        setEditingPosIdx={setEditingPosIdx}
                        setBattingOrder={setBattingOrder}
                        pitchers={starters}
                        battingOrder={battingOrder}
                        commitLineupUpdate={commitLineupUpdate}
                        simEvents={simEvents}
                        simBaserunningEvents={simBaserunningEvents}
                        simFieldingEvents={simFieldingEvents}
                        lineupEditsDisabled={dndDisabled}
                      />
                    ))}
                  </Tbody>
                </Table>
              <DefColumnFootnote />
              <Box mt={4}>
                {starters.length > 0 ? (
                  <>
                    <Divider my={4} borderColor="gray.700" />
                    <PitchersSection
                      pitchers={startersWithPitcherStats}
                      onDropToPitchers={onDropToPitchers}
                      battingOrder={battingOrder}
                      movePitcher={movePitcher}
                      onSwitchToPitchersTab={onSwitchToPitchersTab}
                      lineupEditsDisabled={dndDisabled}
                    />
                  </>
                ) : (
                  <Box mt={2}>
                    <Text color={darkSecondary} mb={2}>No Starting Pitchers Available</Text>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={onSwitchToPitchersTab ?? (() => { window.location.href = '/roster?tab=Pitchers'; })}
                    >
                      Manage Your Pitchers
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <BenchSection
              bench={benchState}
              columnHeaders={columnHeaders}
              onDropToBench={onDropToBench}
              battingOrder={battingOrder}
              simEvents={simEvents}
              simBaserunningEvents={simBaserunningEvents}
              simFieldingEvents={simFieldingEvents}
              lineupEditsDisabled={dndDisabled}
              onLoadOtherPlayers={onLoadOtherPlayers}
              isLoadingOtherPlayers={isLoadingOtherPlayers}
              showLoadOtherPlayers={!otherPlayersLoaded}
              otherPlayers={otherPlayersState}
              otherSimEvents={otherSimEvents}
              otherSimBaserunningEvents={otherSimBaserunningEvents}
              otherSimFieldingEvents={otherSimFieldingEvents}
            />
          </Flex>
          {/* Inactive Players */}
          {/* <Box mb={4}>
            <Text fontWeight="bold" mb={2}>Inactive Players</Text>
            <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
              <Thead bg={darkTableBg}>
                <Tr>
                  <Th color={darkText} fontSize="sm" px={2} py={1}>Player</Th>
                  <Th color={darkText} fontSize="sm" px={2} py={1}>Pos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fakeInactivePlayers.map((p, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm" px={2} py={1}>{p.name}</Td>
                    <Td fontSize="sm" px={2} py={1}>{p.position}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box> */}
          </TabPanel>
          <TabPanel>
            {/* 9 man TabPanel content as before */}
            <Text color="red.400" fontWeight="bold" mb={2}>
              {nineManMissingPositions.length > 0 ? `Lineup incomplete. Position(s) missing: ${nineManMissingPositions.join(' ')}` : ''}
            </Text>
            {nineManDuplicatePositions.length > 0 && (
              <Text color="red.400" fontWeight="bold" mb={2}>
                {`There are multiple players at: ${nineManDuplicatePositions.join(' ')}`}
              </Text>
            )}
            <Flex gap={8}>
              {/* Left column: Batting Order + Pitchers */}
              <Box flex="2">
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="bold">Batting order</Text>
                  <Flex align="center" gap={1} color={darkSecondary} fontSize="xs">
                    <Icon as={LockIcon} boxSize={3} />
                    <Text>= No PH for this batter</Text>
                  </Flex>
                </Flex>
                <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
                  <Thead bg={darkTableBg}>
                    <Tr>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>#</Th>
                      <Th color={darkText} fontSize="sm" px={1} py={1} w={8} textAlign="center">
                        <Icon as={LockIcon} boxSize={3} />
                      </Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
                      <Th color={darkText} fontSize="sm" px={2} py={1}>POS</Th>
                      {columnHeaders.slice(1).map((col: string) => (
                        <Th key={col} color={darkText} textAlign="center" fontSize="sm" px={2} py={1} whiteSpace="nowrap">{batterStatColumnHeaderNode(col)}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {battingOrderWithSlots.map((slot, idx) => (
                      <BattingOrderRow
                        key={idx}
                        slot={slot}
                        idx={idx}
                        moveBatter={moveBatter}
                        onDropFromBench={onDropFromBench}
                        positions={positions}
                        columnHeaders={columnHeaders}
                        editingPosIdx={editingPosIdx}
                        setEditingPosIdx={setEditingPosIdx}
                        setBattingOrder={setBattingOrder}
                        pitchers={starters}
                        battingOrder={battingOrder}
                        commitLineupUpdate={commitLineupUpdate}
                        simEvents={simEvents}
                        simBaserunningEvents={simBaserunningEvents}
                        simFieldingEvents={simFieldingEvents}
                        lineupEditsDisabled={dndDisabled}
                      />
                    ))}
                  </Tbody>
                </Table>
              <DefColumnFootnote />
              <Box mt={4}>
                {starters.length > 0 ? (
                  <>
                    <Divider my={4} borderColor="gray.700" />
                    <PitchersSection
                      pitchers={startersWithPitcherStats}
                      onDropToPitchers={onDropToPitchers}
                      battingOrder={battingOrder}
                      movePitcher={movePitcher}
                      onSwitchToPitchersTab={onSwitchToPitchersTab}
                      lineupEditsDisabled={dndDisabled}
                    />
                  </>
                ) : (
                  <Box mt={2}>
                    <Text color={darkSecondary} mb={2}>No Starting Pitchers Available</Text>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={onSwitchToPitchersTab ?? (() => { window.location.href = '/roster?tab=Pitchers'; })}
                    >
                      Manage Your Pitchers
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <BenchSection
              bench={benchState}
              columnHeaders={columnHeaders}
              onDropToBench={onDropToBench}
              battingOrder={battingOrder}
              simEvents={simEvents}
              simBaserunningEvents={simBaserunningEvents}
              simFieldingEvents={simFieldingEvents}
              lineupEditsDisabled={dndDisabled}
              onLoadOtherPlayers={onLoadOtherPlayers}
              isLoadingOtherPlayers={isLoadingOtherPlayers}
              showLoadOtherPlayers={!otherPlayersLoaded}
              otherPlayers={otherPlayersState}
              otherSimEvents={otherSimEvents}
              otherSimBaserunningEvents={otherSimBaserunningEvents}
              otherSimFieldingEvents={otherSimFieldingEvents}
            />
          </Flex>
          {/* Inactive Players */}
          {/* <Box mb={4}>
            <Text fontWeight="bold" mb={2}>Inactive Players</Text>
            <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
              <Thead bg={darkTableBg}>
                <Tr>
                  <Th color={darkText} fontSize="sm" px={2} py={1}>Player</Th>
                  <Th color={darkText} fontSize="sm" px={2} py={1}>Pos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fakeInactivePlayers.map((p, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm" px={2} py={1}>{p.name}</Td>
                    <Td fontSize="sm" px={2} py={1}>{p.position}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box> */}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

export default LineupManagement; 